import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Chip, IconButton, Tooltip,
  CircularProgress, Button, Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent,
} from "@mui/material";
import {
  Refresh, CheckCircle, Error, Warning, ShoppingCart,
  Cable, Lock, LockOpen, Visibility, VisibilityOff,
  OpenInNew, Receipt,
} from "@mui/icons-material";
import { hubApi } from "../services/hubApi";

const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", color: "#25D366", icon: "WA" },
  whatsapp_oficial: { label: "WhatsApp Oficial", color: "#25D366", icon: "WA" },
  facebook: { label: "Facebook", color: "#1877F2", icon: "FB" },
  instagram: { label: "Instagram", color: "#E1306C", icon: "IG" },
  telegram: { label: "Telegram", color: "#0088CC", icon: "TG" },
  email: { label: "E-mail", color: "#EA4335", icon: "EM" },
  voip: { label: "VoIP", color: "#6B48FF", icon: "VP" },
  sip: { label: "SIP", color: "#FF6B00", icon: "SP" },
  tiktok: { label: "TikTok", color: "#000", icon: "TK" },
  sms: { label: "SMS", color: "#4CAF50", icon: "SM" },
  google_business: { label: "Google Business", color: "#4285F4", icon: "GB" },
  mercadolivre: { label: "Mercado Livre", color: "#FFE600", icon: "ML" },
  teams: { label: "MS Teams", color: "#6264A7", icon: "TM" },
};

const STATUS_CONFIG = {
  active: { label: "Ativo", color: "#25D366", bg: "rgba(37,211,102,0.1)" },
  healthy: { label: "Ativo", color: "#25D366", bg: "rgba(37,211,102,0.1)" },
  grace_period: { label: "Grace Period", color: "#F59E0B", bg: "rgba(245,158,11,0.1)" },
  suspended: { label: "Suspenso", color: "#EF4444", bg: "rgba(239,68,68,0.1)" },
  readonly: { label: "Somente Leitura", color: "#6B48FF", bg: "rgba(107,72,255,0.1)" },
  expired: { label: "Expirado", color: "#EF4444", bg: "rgba(239,68,68,0.15)" },
  revoked: { label: "Revogado", color: "#EF4444", bg: "rgba(239,68,68,0.15)" },
  unknown: { label: "Desconhecido", color: "#6B7280", bg: "rgba(107,114,128,0.1)" },
};

function ChannelCard({ channel, subscription, connection, onContract }) {
  const meta = CHANNEL_META[channel] || { label: channel, color: "#6B7280", icon: "?" };
  const isActive = subscription?.active === true;
  const tokenStatus = connection?.tokenStatus || "unknown";
  const statusCfg = STATUS_CONFIG[isActive ? (tokenStatus || "healthy") : "expired"] || STATUS_CONFIG.unknown;
  const healthScore = connection?.healthScore ?? (isActive ? 100 : 0);

  return (
    <Card sx={{
      bgcolor: "#111827",
      border: `1px solid ${isActive ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)"}`,
      borderRadius: 3,
      opacity: isActive ? 1 : 0.6,
      transition: "all 0.2s",
      "&:hover": { borderColor: isActive ? meta.color + "40" : "rgba(255,255,255,0.08)" },
    }}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 2,
              bgcolor: meta.color + "20",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 800, color: meta.color,
            }}>
              {meta.icon}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#fff" }}>
                {meta.label}
              </Typography>
              {connection && (
                <Typography sx={{ fontSize: 11, color: "#6B7280" }}>
                  {connection.name}
                </Typography>
              )}
            </Box>
          </Box>
          <Chip
            size="small"
            label={isActive ? statusCfg.label : "Nao contratado"}
            sx={{ fontSize: 10, height: 22, bgcolor: statusCfg.bg, color: statusCfg.color }}
          />
        </Box>

        {/* Info */}
        {isActive && (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Typography sx={{ fontSize: 11, color: "#6B7280" }}>Health Score</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: healthScore > 60 ? "#25D366" : healthScore > 30 ? "#F59E0B" : "#EF4444" }}>
                {healthScore}/100
              </Typography>
            </Box>
            {/* Health bar */}
            <Box sx={{ width: "100%", height: 4, bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
              <Box sx={{
                width: `${healthScore}%`, height: "100%", borderRadius: 2,
                bgcolor: healthScore > 60 ? "#25D366" : healthScore > 30 ? "#F59E0B" : "#EF4444",
                transition: "width 0.3s",
              }} />
            </Box>

            {subscription?.expiresAt && (
              <Typography sx={{ fontSize: 10, color: "#6B7280", mt: 0.5 }}>
                Expira: {new Date(subscription.expiresAt).toLocaleDateString()}
              </Typography>
            )}
            {subscription?.maxConnections && (
              <Typography sx={{ fontSize: 10, color: "#6B7280" }}>
                Max conexoes: {subscription.maxConnections}
              </Typography>
            )}
            {subscription?.isTrial && (
              <Chip size="small" label="Trial" sx={{ fontSize: 9, height: 18, mt: 0.5, bgcolor: "rgba(107,72,255,0.1)", color: "#6B48FF" }} />
            )}
          </Box>
        )}

        {/* Action */}
        {!isActive && (
          <Button
            fullWidth
            size="small"
            startIcon={<ShoppingCart sx={{ fontSize: 14 }} />}
            onClick={() => onContract(channel)}
            sx={{
              mt: 1, textTransform: "none", fontSize: 12, fontWeight: 600,
              color: "#6B48FF", borderColor: "rgba(107,72,255,0.3)",
              border: "1px solid rgba(107,72,255,0.3)", borderRadius: 2,
              "&:hover": { bgcolor: "rgba(107,72,255,0.08)" },
            }}
          >
            Contratar
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function BillingPage({ showSnackbar }) {
  const [channels, setChannels] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);
  const [marketplaceUrl, setMarketplaceUrl] = useState("");
  const [marketplaceChannel, setMarketplaceChannel] = useState("");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [chResp, dashResp, connResp] = await Promise.all([
        hubApi.getChannels(),
        hubApi.getDashboard(),
        hubApi.getConnections(),
      ]);
      setChannels(chResp.data.channels || []);
      setDashboard(dashResp.data);
      setConnections(connResp.data.connections || []);
    } catch (err) {
      setError("Erro ao carregar dados de billing");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleContract = async (channel) => {
    try {
      const { data } = await hubApi.getPortalRedirect(channel);
      if (data.redirectUrl) {
        setMarketplaceChannel(channel);
        setMarketplaceUrl(data.redirectUrl);
        setMarketplaceOpen(true);
      } else {
        showSnackbar?.("Marketplace indisponivel", "error");
      }
    } catch {
      showSnackbar?.("Erro ao abrir marketplace", "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#6B48FF" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Error sx={{ fontSize: 48, color: "#EF4444", mb: 1 }} />
        <Typography sx={{ color: "#EF4444" }}>{error}</Typography>
        <Button onClick={loadData} sx={{ mt: 2, color: "#6B48FF" }}>Tentar novamente</Button>
      </Box>
    );
  }

  const billing = dashboard?.billing || {};
  const activeChannels = channels.filter((c) => c.enabled);
  const inactiveChannels = channels.filter((c) => !c.enabled);
  const billingAlerts = (billing.grace || 0) + (billing.suspended || 0) + (billing.readonly || 0);

  // Montar mapa de subscriptions a partir dos channels
  const subscriptionMap = {};
  channels.forEach((ch) => {
    const key = ch.id === "whatsapp_oficial" ? "whatsapp" : ch.id;
    subscriptionMap[ch.id] = {
      active: ch.enabled,
      // Dados extras vem do dashboard se disponível
    };
  });

  // Montar mapa de connections por canal
  const connectionMap = {};
  connections.forEach((c) => {
    connectionMap[c.channel] = c;
  });

  // Audit logs de billing
  const billingAudits = (dashboard?.recentAlerts || []).filter((a) =>
    a.action?.startsWith("billing.") || a.action?.includes("plan.")
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff" }}>Billing</Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            {activeChannels.length} canais ativos de {channels.length}
          </Typography>
        </Box>
        <Tooltip title="Atualizar">
          <IconButton onClick={loadData} sx={{ color: "#6B7280" }}><Refresh /></IconButton>
        </Tooltip>
      </Box>

      {/* Billing alerts */}
      {billingAlerts > 0 && (
        <Card sx={{ bgcolor: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)", borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Warning sx={{ fontSize: 18, color: "#F59E0B" }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#F59E0B" }}>
                Atencao — Canais com restricao
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {billing.grace > 0 && <Chip size="small" label={`${billing.grace} em grace period`} sx={{ bgcolor: "rgba(245,158,11,0.15)", color: "#F59E0B", fontSize: 11 }} />}
              {billing.suspended > 0 && <Chip size="small" label={`${billing.suspended} suspenso(s)`} sx={{ bgcolor: "rgba(239,68,68,0.15)", color: "#EF4444", fontSize: 11 }} />}
              {billing.readonly > 0 && <Chip size="small" label={`${billing.readonly} somente leitura`} sx={{ bgcolor: "rgba(107,72,255,0.15)", color: "#6B48FF", fontSize: 11 }} />}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: "rgba(37,211,102,0.06)", border: "1px solid rgba(37,211,102,0.15)", borderRadius: 3 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography sx={{ fontSize: 11, color: "#6B7280" }}>Canais Ativos</Typography>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#25D366" }}>{activeChannels.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: "rgba(107,72,255,0.06)", border: "1px solid rgba(107,72,255,0.15)", borderRadius: 3 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography sx={{ fontSize: 11, color: "#6B7280" }}>Disponiveis</Typography>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#6B48FF" }}>{inactiveChannels.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)", borderRadius: 3 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography sx={{ fontSize: 11, color: "#6B7280" }}>Em Alerta</Typography>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: billingAlerts > 0 ? "#F59E0B" : "#6B7280" }}>{billingAlerts}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography sx={{ fontSize: 11, color: "#6B7280" }}>Conexoes</Typography>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#D1D5DB" }}>{connections.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Canais contratados */}
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#fff", mb: 2 }}>
        Canais Contratados
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {activeChannels.map((ch) => (
          <Grid item xs={12} sm={6} md={4} key={ch.id}>
            <ChannelCard
              channel={ch.id}
              subscription={subscriptionMap[ch.id]}
              connection={connectionMap[ch.id] || connectionMap[ch.id === "whatsapp_oficial" ? "whatsapp_oficial" : ch.id]}
              onContract={handleContract}
            />
          </Grid>
        ))}
        {activeChannels.length === 0 && (
          <Grid item xs={12}>
            <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
              <CardContent sx={{ py: 4, textAlign: "center" }}>
                <Typography sx={{ color: "#6B7280" }}>Nenhum canal contratado</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Canais disponíveis */}
      {inactiveChannels.length > 0 && (
        <>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#9CA3AF", mb: 2 }}>
            Disponiveis para Contratacao
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {inactiveChannels.map((ch) => (
              <Grid item xs={12} sm={6} md={4} key={ch.id}>
                <ChannelCard
                  channel={ch.id}
                  subscription={subscriptionMap[ch.id]}
                  connection={null}
                  onContract={handleContract}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Historico billing */}
      {billingAudits.length > 0 && (
        <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 1 }}>
              <Receipt sx={{ fontSize: 16, color: "#6B7280" }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>Historico de Billing (24h)</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)" }}>Evento</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)" }}>Detalhes</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)", width: 100 }}>Horario</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingAudits.slice(0, 10).map((log, i) => {
                  const details = log.details || {};
                  return (
                    <TableRow key={i}>
                      <TableCell sx={{ color: "#D1D5DB", fontSize: 12, borderColor: "rgba(255,255,255,0.06)", fontFamily: "monospace" }}>
                        {log.action}
                      </TableCell>
                      <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)" }}>
                        {details.channel || details.channelKey || "—"}
                      </TableCell>
                      <TableCell sx={{ color: "#6B7280", fontSize: 12, borderColor: "rgba(255,255,255,0.06)" }}>
                        {new Date(log.createdAt).toLocaleTimeString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Marketplace iframe dialog */}
      <Dialog
        open={marketplaceOpen}
        onClose={() => setMarketplaceOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#111827", borderRadius: 3, height: "80vh" } }}
      >
        <DialogTitle sx={{ color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ShoppingCart sx={{ fontSize: 20, color: "#6B48FF" }} />
            Contratar {CHANNEL_META[marketplaceChannel]?.label || marketplaceChannel}
          </Box>
          <IconButton onClick={() => setMarketplaceOpen(false)} sx={{ color: "#6B7280" }}>✕</IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {marketplaceUrl && (
            <iframe
              src={marketplaceUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="Marketplace"
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
